import { useCallback, useEffect, useRef, useState } from "react";
import supernovaChain, { SUPERNOVA_ERROR, toSupernovaWriteError } from "./supernovaChain";
import { TX, isTxBusy, isTxSettled } from "./useSupernovaStake";

/**
 * useSupernovaUnstake — the state machine behind the unstake modal.
 *
 * It is the stake flow's machine with one moving part instead of three: there is
 * no approval step, because the staking contract is already holding the NFTs it
 * would be returning. ERC-1155 operator approval gates NFTs LEAVING the member's
 * wallet, and an unstake moves them the other way.
 *
 * THE TRANSACTION STATES ARE THE STAKE FLOW'S, IMPORTED RATHER THAN REDECLARED
 * (useSupernovaStake.js documents each one against the write contract in
 * supernovaChain.js). One vocabulary, so "unavailable" means exactly the same
 * thing on both surfaces:
 *
 *   PENDING     the member is looking at their wallet's signature prompt
 *   SIGNED      they signed; a hash is on the wire, not yet mined
 *   CONFIRMED   mined — only reachable via a `confirmed` promise that resolves
 *   REJECTED    they declined. A decision, not a failure; nothing changed
 *   UNAVAILABLE the service reports NOT_IMPLEMENTED — this build genuinely has
 *               no signing path (no verified ABI compiled in, or no browser
 *               wallet). NEVER a success, and never presented as one
 *   ERROR       something genuinely went wrong; member-safe copy, no stack
 *
 * NON-CUSTODIAL. Nothing here signs. Unstaking is a transaction the member signs
 * in their own wallet; ARC Educational holds no key, cannot move an NFT, and
 * cannot return one on anybody's behalf.
 *
 * IT CALLS `requestUnstakeSignature`, NOT `unstake`. The seam is where the exit
 * payload is built, logged and cross-checked against the calldata — including
 * WHICH of the two exits is being signed, which matters more here than anywhere
 * else in the app: `unstake` and `emergencyUnstake` take identical calldata, so
 * only that check ties the signature back to the screen the member read.
 *
 * NOTHING RETRIES BY ITSELF. A declined signature resets to the quote and waits
 * for the member to decide again.
 *
 * NOT AN ENTITLEMENT. The lot being closed comes from the chain. Nothing in this
 * hook reads or writes a membership record.
 *
 * THE QUOTE IS NOT THE SETTLEMENT. The XP figures the modal shows come from the
 * model (supernovaExitQuote); what is actually released is decided on-chain.
 * This hook deliberately carries no XP: it would be a number we invented at the
 * moment of signing.
 */

export { TX, isTxBusy, isTxSettled };

/* Copy is duplicated from useSupernovaStake rather than shared, so that flow's
   tested behaviour is untouched by this one. The two must stay word-identical —
   they describe the same wallet interaction. */
const PROMPT_MESSAGE =
  "Confirm the request in your wallet. Nothing is submitted until you sign.";
const SUBMITTED_MESSAGE =
  "Signed and submitted to Arbitrum. Waiting for it to be confirmed on-chain.";
const NO_WATCH_MESSAGE =
  "Signed and submitted to Arbitrum. We can't follow it to confirmation from " +
  "here — check your wallet or a block explorer for the final result.";

const IDLE_TX = { state: TX.IDLE, message: null, hash: null, error: null };

const txFromError = (error) => {
  if (error && error.code === SUPERNOVA_ERROR.NOT_IMPLEMENTED) {
    return { state: TX.UNAVAILABLE, message: error.message, hash: null, error };
  }
  if (error && error.code === SUPERNOVA_ERROR.USER_REJECTED) {
    return { state: TX.REJECTED, message: error.message, hash: null, error };
  }
  return { state: TX.ERROR, message: error.message, hash: null, error };
};

/**
 * @param {string|null} wallet member's address
 * @param {Object} [options]
 * @param {boolean} [options.enabled] false ⇒ the hook does nothing at all.
 * @param {Object} [options.service] injectable supernovaChain surface (tests)
 */
export default function useSupernovaUnstake(wallet, options = {}) {
  const { enabled = false, service = supernovaChain } = options;
  const active = Boolean(enabled && wallet);

  const [unstakeTx, setUnstakeTx] = useState(IDLE_TX);

  /* Invalidates anything still in flight, so a call that resolves after the
     modal closed (or after the wallet changed) can't write over current state. */
  const runRef = useRef(0);
  useEffect(() => () => { runRef.current += 1; }, []);

  useEffect(() => {
    if (!active) {
      runRef.current += 1;
      setUnstakeTx(IDLE_TX);
    }
  }, [active]);

  /**
   * Close a lot. The member signs it in their own wallet.
   *
   * @param {{lotId: string, emergency?: boolean}} request `emergency` marks the
   *        pre-unlock exit, which is a different contract function; the modal
   *        sets it, because it is the surface that told the member which exit
   *        they are taking. The contract closes whole lots only.
   */
  const submit = useCallback(
    async (request) => {
      if (!active) return { ok: false };
      if (!request || !request.lotId) {
        /* We don't know which position we'd be closing. Refusing is the only
           safe answer — the alternative is a signature over the wrong lot. */
        const error = {
          code: SUPERNOVA_ERROR.WRITE_FAILED,
          message:
            "We couldn't identify which stake to close, so nothing was " +
            "submitted. Reload the tab and try again.",
        };
        setUnstakeTx({ state: TX.ERROR, message: error.message, hash: null, error });
        return { ok: false, error };
      }

      const runId = (runRef.current += 1);
      const current = () => runRef.current === runId;

      setUnstakeTx({ state: TX.PENDING, message: PROMPT_MESSAGE, hash: null, error: null });

      let result;
      try {
        result = await service.requestUnstakeSignature({ wallet, ...request });
      } catch (raw) {
        const error = toSupernovaWriteError(
          raw,
          "Couldn't submit your unstake. Nothing was released — try again."
        );
        if (current()) setUnstakeTx(txFromError(error));
        return { ok: false, error };
      }

      const hash = result?.hash || null;
      const canWatch = typeof result?.confirmed?.then === "function";

      if (!canWatch) {
        /* TODO(backend, supervised): a service that resolves without a
           `confirmed` promise leaves us at "submitted" for good. That is the
           honest end of the line — do not promote it to confirmed here. */
        if (current()) {
          setUnstakeTx({ state: TX.SIGNED, message: NO_WATCH_MESSAGE, hash, error: null });
        }
        return { ok: false, signed: true, hash };
      }

      if (current()) {
        setUnstakeTx({ state: TX.SIGNED, message: SUBMITTED_MESSAGE, hash, error: null });
      }

      try {
        await result.confirmed;
      } catch (raw) {
        const error = toSupernovaWriteError(
          raw,
          "Your unstake didn't go through. Check your wallet or a block explorer."
        );
        if (current()) setUnstakeTx({ ...txFromError(error), hash });
        return { ok: false, error, hash };
      }

      if (current()) {
        setUnstakeTx({ state: TX.CONFIRMED, message: null, hash, error: null });
      }
      return { ok: true, hash };
    },
    [active, service, wallet]
  );

  /** Back to the quote with nothing lost — a decline is fully recoverable. */
  const resetTx = useCallback(() => setUnstakeTx(IDLE_TX), []);
  const reset = useCallback(() => {
    runRef.current += 1;
    setUnstakeTx(IDLE_TX);
  }, []);

  return { unstakeTx, submit, resetTx, reset, busy: isTxBusy(unstakeTx.state) };
}
